package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.logic.NavigatorSelectPage
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel
import com.example.mystybag.appCore.model.pages.DeliveryPageUIState
import com.example.mystybag.appCore.model.pages.ReservationCardExtra
import com.example.mystybag.communication.common.CommonCommands.getBagDetails
import com.example.mystybag.communication.common.CommonCommands.getMerchantLocations
import com.example.mystybag.communication.common.CommonCommands.getUserReservationsCommand
import com.example.mystybag.communication.common.CommonCommands.updateReservationStatusCommand
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.logic.base.MainCallback
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.expected.openLargeMap
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.sharedmodels.project.database.UserReservationModel
import com.example.mystybag.sharedmodels.project.logic.enums.ReservationStatus
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantLocationsRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.reservations.UpdateReservationStatusRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.LocationRatingResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantLocationsResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.reservations.GetUserReservationsResponseModel

class DeliveryPageViewModel(context: AppContext): ChildViewModel<DeliveryPageUIState>(context, DeliveryPageUIState()) {
    private var hasLoadedOnce = false
    private val merchantLocationsCache = mutableMapOf<Long, List<com.example.mystybag.sharedmodels.project.responseModels.merchant.MerchantLocationResponseModel>>()
    private val merchantRatingsByLocationCache = mutableMapOf<Long, Map<Long, LocationRatingResponseModel>>()
    private val bagCollectWindowCache = mutableMapOf<Long, String?>()
    /** bagId -> title from `getBagDetails`; key present means fetch completed (value may be null). */
    private val bagTitleCache = mutableMapOf<Long, String?>()
    private val inFlightMerchantRequests = mutableSetOf<Long>()
    private val inFlightBagRequests = mutableSetOf<Long>()

    override fun onCreate() {
        navControls.title = sharedString(SR.strings.my_reservations)
    }

    fun onTabSelected(index: Int) {
        updateState { it.copy(selectedTabIndex = index) }
    }

    fun consumeScrollHistoryToTopPending() {
        updateState { it.copy(scrollHistoryToTopPending = false) }
    }

    override fun onResume() {
        // Lazy-load only when tab is actually opened to reduce startup network waterfall.
        if (!hasLoadedOnce) {
            loadReservations(showLoading = true)
        } else {
            // Refresh silently when returning to avoid list flicker/loader flash.
            loadReservations(showLoading = uiState.value.reservations.isEmpty())
        }
    }

    fun onReservationClick(reservation: UserReservationModel) {
        if (reservation.isReviewGiven == false && (reservation.status == ReservationStatus.DELIVERED_TO_CLIENT.code || reservation.status == ReservationStatus.PAID_TO_MERCHANT.code)) {
            navigate(com.example.mystybag.appCore.logic.NavigatorLeaveReview(reservation = reservation))
        } else {
            onReservationDetailsClick(reservation)
        }
    }

    fun onReservationDetailsClick(reservation: UserReservationModel) {
        parentOpenBottomSheet(
            com.example.mystybag.appCore.logic.BSNavigatorReservationDetails(
                reservation = reservation
            )
        )
    }

    /** Same action as "Unlock for delivery" in reservation details: mark reservation as received by client. */
    fun onUnlockForDeliveryClick(reservation: UserReservationModel) {
        commandBlock(showProgress = false, hideProgress = false) {
            updateReservationStatusCommand(
                UpdateReservationStatusRequestModel(
                    reservationId = reservation.id,
                    status = ReservationStatus.DELIVERED_TO_CLIENT.code
                )
            ).onResponse(
                success = {
                    updateState { it.copy(selectedTabIndex = 1) }
                    // Scroll-to-top must run after refreshed list includes DELIVERED_TO_CLIENT;
                    // otherwise LaunchedEffect fires on stale data, consumes the flag, and the new row at index 0 is missed.
                    loadReservations(showLoading = false, scrollHistoryToTopAfterLoad = true)
                },
                error = { error ->
                    updateState {
                        it.copy(errorMessage = error.statusMessage ?: "Failed to update reservation status")
                    }
                }
            )
        }
    }

    fun refreshReservations() {
        loadReservations(showLoading = uiState.value.reservations.isEmpty())
    }

    fun onPickupAddressClick(reservationId: Long) {
        val extra = uiState.value.reservationExtras[reservationId] ?: return
        val address = extra.pickupAddress?.takeIf { it.isNotBlank() } ?: return
        val lat = extra.locationLatitude
        val lon = extra.locationLongitude
        if (lat != 0.0 && lon != 0.0) {
            openLargeMap(lat, lon, address)
        } else {
            openLargeMap(0.0, 0.0, address)
        }
    }

    /** Navigate to Find tab (from empty state "Find bag" button). */
    fun onFindBagClick() {
        setResult<MainCallback> {
            popToMainAndNavigate(NavigatorSelectPage(MystyBagDigitalWalletViewModel.PAGE_HOME))
        }
    }

    private fun loadReservations(showLoading: Boolean, scrollHistoryToTopAfterLoad: Boolean = false) {
        hasLoadedOnce = true
        if (showLoading) {
            updateState { it.copy(isLoading = true, errorMessage = null) }
        } else {
            // Keep current list visible while doing a background refresh.
            updateState { it.copy(errorMessage = null) }
        }

        commandBlock(showProgress = false, hideProgress = false) {
            getUserReservationsCommand().onResponse(
                success = { response: GetUserReservationsResponseModel ->
                    val list = response.reservationsList.sortedByDescending { it.id }
                    val reservationIds = list.map { it.id }.toSet()
                    updateState {
                        it.copy(
                            isLoading = false,
                            reservations = list,
                            reservationExtras = it.reservationExtras.filterKeys { id -> id in reservationIds },
                            errorMessage = null,
                            scrollHistoryToTopPending = if (scrollHistoryToTopAfterLoad) {
                                true
                            } else {
                                it.scrollHistoryToTopPending
                            }
                        )
                    }
                    list.forEach { reservation ->
                        loadReservationExtra(reservation)
                    }
                },
                error = { error ->
                    updateState {
                        it.copy(
                            isLoading = false,
                            errorMessage = error.statusMessage ?: "Failed to load reservations"
                        )
                    }
                }
            )
        }
    }

    private fun loadReservationExtra(reservation: UserReservationModel) {
        val rid = reservation.id
        val currentExtra = uiState.value.reservationExtras[rid]
        val needsAddress = currentExtra?.pickupAddress.isNullOrBlank()
        val needsLocationRating = currentExtra?.locationAverageRating == null
        val needsCollectWindow = currentExtra?.collectWindowLabel.isNullOrBlank()

        if (reservation.merchantId != 0L && (needsAddress || needsLocationRating)) {
            val merchantId = reservation.merchantId

            merchantLocationsCache[merchantId]?.let { cachedLocations ->
                applyLocationExtraToAllReservations(
                    merchantId = merchantId,
                    locations = cachedLocations,
                    ratingsByLocation = merchantRatingsByLocationCache[merchantId].orEmpty()
                )
            }
            if (merchantId !in merchantLocationsCache && inFlightMerchantRequests.add(merchantId)) {
                commandBlock(showProgress = false, hideProgress = false) {
                    getMerchantLocations(GetMerchantLocationsRequestModel(merchantId = merchantId))
                        .onResponse(
                            success = { response ->
                                inFlightMerchantRequests.remove(merchantId)
                                val responseModel = response as? GetMerchantLocationsResponseModel
                                val locations = responseModel?.locations ?: emptyList()
                                val ratingsByLocation = responseModel?.ratingsByLocation
                                    ?.mapKeys { it.key.toLongOrNull() ?: 0L }
                                    ?.filterKeys { it != 0L }
                                    ?: emptyMap()
                                merchantLocationsCache[merchantId] = locations
                                merchantRatingsByLocationCache[merchantId] = ratingsByLocation
                                applyLocationExtraToAllReservations(
                                    merchantId = merchantId,
                                    locations = locations,
                                    ratingsByLocation = ratingsByLocation
                                )
                            },
                            error = {
                                inFlightMerchantRequests.remove(merchantId)
                            }
                        )
                }
            }
        }
        if (reservation.bagId != 0L) {
            val bagId = reservation.bagId
            val needsBagTitleFallback = reservation.bagName.isNullOrBlank()

            if (bagCollectWindowCache.containsKey(bagId)) {
                bagCollectWindowCache[bagId]?.let { label ->
                    applyCollectWindowToAllReservations(bagId, label)
                }
            }
            if (bagTitleCache.containsKey(bagId)) {
                bagTitleCache[bagId]?.takeIf { it.isNotBlank() }?.let { title ->
                    applyBagTitleFromDetailsToAllReservations(bagId, title)
                }
            }

            val needFetchCollect = needsCollectWindow && !bagCollectWindowCache.containsKey(bagId)
            val needFetchTitle = needsBagTitleFallback && !bagTitleCache.containsKey(bagId)
            if ((needFetchCollect || needFetchTitle) && inFlightBagRequests.add(bagId)) {
                commandBlock(showProgress = false, hideProgress = false) {
                    getBagDetails(bagId).onResponse(
                        success = { response ->
                            inFlightBagRequests.remove(bagId)
                            val bag = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetBagDetailsResponseModel ?: return@onResponse
                            val from = bag.pickupTimeFrom?.takeIf { it.isNotBlank() }
                            val to = bag.pickupTimeTo?.takeIf { it.isNotBlank() }
                            val collectLabel = when {
                                from != null && to != null -> "$from – $to"
                                from != null -> from
                                to != null -> to
                                else -> null
                            }
                            bagCollectWindowCache[bagId] = collectLabel
                            if (collectLabel != null) applyCollectWindowToAllReservations(bagId, collectLabel)

                            val resolvedTitle = bag.title?.takeIf { it.isNotBlank() }
                            bagTitleCache[bagId] = resolvedTitle
                            if (resolvedTitle != null) {
                                applyBagTitleFromDetailsToAllReservations(bagId, resolvedTitle)
                            }
                        },
                        error = {
                            inFlightBagRequests.remove(bagId)
                        }
                    )
                }
            }
        }
    }

    private fun applyLocationExtraToAllReservations(
        merchantId: Long,
        locations: List<com.example.mystybag.sharedmodels.project.responseModels.merchant.MerchantLocationResponseModel>,
        ratingsByLocation: Map<Long, LocationRatingResponseModel>
    ) {
        if (locations.isEmpty() && ratingsByLocation.isEmpty()) return

        updateState { state ->
            val updates = state.reservations
                .filter { it.merchantId == merchantId }
                .mapNotNull { reservation ->
                    val current = state.reservationExtras[reservation.id] ?: ReservationCardExtra()
                    val loc = locations.find { it.id == reservation.merchantLocationId } ?: locations.firstOrNull()

                    var updated = current
                    var changed = false

                    if (current.pickupAddress.isNullOrBlank() && loc != null) {
                        val address = loc.address?.takeIf { it.isNotBlank() }
                        if (address != null) {
                            val lat = loc.latitude.toDouble().takeIf { it != -1.0 } ?: 0.0
                            val lon = loc.longitude.toDouble().takeIf { it != -1.0 } ?: 0.0
                            updated = updated.copy(
                                pickupAddress = address,
                                locationLatitude = lat,
                                locationLongitude = lon
                            )
                            changed = true
                        }
                    }

                    if (current.locationAverageRating == null) {
                        val locationRating = ratingsByLocation[reservation.merchantLocationId]?.averageRating?.coerceIn(0.0, 5.0)
                        if (locationRating != null) {
                            updated = updated.copy(locationAverageRating = locationRating)
                            changed = true
                        }
                    }

                    if (changed) reservation.id to updated else null
                }
            if (updates.isEmpty()) state else state.copy(reservationExtras = state.reservationExtras + updates)
        }
    }

    private fun applyCollectWindowToAllReservations(bagId: Long, collectLabel: String) {
        updateState { state ->
            val updates = state.reservations
                .filter { it.bagId == bagId }
                .mapNotNull { reservation ->
                    val current = state.reservationExtras[reservation.id] ?: ReservationCardExtra()
                    if (!current.collectWindowLabel.isNullOrBlank()) return@mapNotNull null
                    reservation.id to current.copy(collectWindowLabel = collectLabel)
                }
            if (updates.isEmpty()) state else state.copy(reservationExtras = state.reservationExtras + updates)
        }
    }

    private fun applyBagTitleFromDetailsToAllReservations(bagId: Long, title: String) {
        updateState { state ->
            val updates = state.reservations
                .filter { it.bagId == bagId && it.bagName.isNullOrBlank() }
                .mapNotNull { reservation ->
                    val current = state.reservationExtras[reservation.id] ?: ReservationCardExtra()
                    if (!current.bagTitleFromDetails.isNullOrBlank()) return@mapNotNull null
                    reservation.id to current.copy(bagTitleFromDetails = title)
                }
            if (updates.isEmpty()) state else state.copy(reservationExtras = state.reservationExtras + updates)
        }
    }
}

